import { Button } from "@resource/atlas/button/Button";
import { Dialog } from "@resource/atlas/dialog";
import { useDialogViews } from "@resource/atlas/dialog/use-dialog-views";
import { ModalName, useModals } from "components/ModalProvider";
import { gql } from "generated/graphql-codegen";
import { ZeusGuideTemplatesForSelectQuery } from "generated/graphql-codegen/graphql";
import { useState } from "react";
import useMutation, { ToastDisplayCondition } from "utils/useMutation";

import ZeusGuideTemplateSelectionView from "./ZeusGuideTemplateSelectionView";

type GuideTemplate = NonNullable<
  ZeusGuideTemplatesForSelectQuery["currentOrganization"]
>["guideTemplates"][number];

const USE_GUIDE_TEMPLATE = gql(`
mutation UseGuideTemplate($input: MapGuideTemplateInput!) {
    mapGuideTemplate(input: $input) {
      message
      code
      success
      jobs {
        ...JobJob
      }
    }
  }
  fragment JobJob on Job {
    id
    guideTemplate {
      id
      name
      isGlobal
      departments {
        id
        name
      }
    }
  }
`);

interface UseGuideTemplateModalProps {
  jobIds: string[];
  onAssign?(): Promise<void> | void;
  guideTemplate?: GuideTemplate | null;
  singleJobName?: string;
  showSuccessToast?: boolean;
  /** Only use if mapping a single job role */
  currentGuideTemplateId?: string;
}

function UseGuideTemplateModal({
  jobIds,
  onAssign,
  guideTemplate,
  showSuccessToast = true,
  currentGuideTemplateId,
}: UseGuideTemplateModalProps) {
  const modalManager = useModals();
  const [selectedGuideTemplate, setSelectedGuideTemplate] =
    useState<GuideTemplate | null>(guideTemplate ?? null);
  const [assignGuideTemplate, { loading: isAssigning }] = useMutation(
    USE_GUIDE_TEMPLATE,
    {
      displayToastsOn: showSuccessToast
        ? ToastDisplayCondition.ALL
        : ToastDisplayCondition.ERROR,
    }
  );
  const onSave = async () => {
    await assignGuideTemplate({
      variables: {
        input: {
          guideTemplateId: selectedGuideTemplate?.id ?? "",
          jobIds,
        },
      },
    });
    await onAssign?.();
    modalManager.close();
  };

  const { dialogContentProps } = useDialogViews(
    {
      templateSelect: {
        size: "large",
        view: ({ closeDialog, initialFocusRef }) => (
          <Dialog.View
            header={{
              title: "Select a template",
            }}
            footer={{
              leftActions: <Button onClick={closeDialog}>Cancel</Button>,
              rightActions: (
                <>
                  <Button
                    ref={initialFocusRef}
                    isLoading={isAssigning}
                    onClick={() => {
                      onSave();
                    }}
                  >
                    Use template
                  </Button>
                </>
              ),
            }}
            hasPadding={false}
          >
            <ZeusGuideTemplateSelectionView
              selectedGuideTemplate={selectedGuideTemplate}
              setSelectedGuideTemplate={setSelectedGuideTemplate}
              currentGuideTemplateId={currentGuideTemplateId}
            />
          </Dialog.View>
        ),
      },
    },
    { initialView: "templateSelect" }
  );

  return (
    <Dialog.Root
      open={modalManager.isActive(ModalName.GUIDE_TEMPLATE_SELECTOR)}
      setOpen={(open) => {
        if (!open) {
          setSelectedGuideTemplate(null);
          modalManager.close();
        }
      }}
    >
      <Dialog.Content size="large" hasPadding={false} {...dialogContentProps} />
    </Dialog.Root>
  );
}

export default UseGuideTemplateModal;
